import {
  DteImportLine,
  DteParseResult,
  DtePaymentCondition,
  DteRawJson,
  ParsedDtePurchase,
} from '../../../../types/restaurant/RestaurantDteImportTypes';

const TIPO_DTE_LABEL: Record<string, string> = {
  '01': 'Factura',
  '03': 'Comprobante de Crédito Fiscal',
};

const num = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const str = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : value == null ? '' : String(value);

const mapCondition = (code: number): DtePaymentCondition => {
  if (code === 1) return 'CONTADO';
  if (code === 2) return 'CREDITO';
  return 'OTRO';
};

/**
 * Convierte un JSON de DTE (Factura 01 / CCF 03) en el modelo que consume el
 * asistente de importación de compras. Nunca lanza: devuelve `{ ok:false, error }`
 * cuando el archivo no tiene la forma esperada.
 */
export const parseDtePurchase = (raw: unknown): DteParseResult => {
  if (!raw || typeof raw !== 'object') {
    return { ok: false, error: 'El archivo no contiene un JSON válido.' };
  }

  const dte = raw as DteRawJson;
  const tipoDte = str(dte.identificacion?.tipoDte);

  if (!TIPO_DTE_LABEL[tipoDte]) {
    return {
      ok: false,
      error:
        'El JSON no parece un DTE de compra. Se admiten Factura (01) o Crédito Fiscal (03).',
    };
  }

  if (!dte.emisor || (!str(dte.emisor.nit) && !str(dte.emisor.nrc))) {
    return {
      ok: false,
      error: 'El DTE no trae los datos del emisor (proveedor).',
    };
  }

  const body = Array.isArray(dte.cuerpoDocumento) ? dte.cuerpoDocumento : [];
  if (body.length === 0) {
    return { ok: false, error: 'El DTE no tiene líneas de detalle.' };
  }

  const lines: DteImportLine[] = body.map((item, index) => {
    const ref = str(item.numItem) || String(index + 1);
    const tributos = Array.isArray(item.tributos) ? item.tributos : [];
    return {
      ref,
      code: str(item.codigo),
      description: str(item.descripcion),
      quantity: num(item.cantidad),
      mhUnit: item.uniMedida == null ? null : num(item.uniMedida),
      unitPrice: num(item.precioUni),
      hasIva: tributos.includes('20'),
      discount: num(item.montoDescu),
      taxedSale: num(item.ventaGravada),
      exemptSale: num(item.ventaExenta),
      nonSubjectSale: num(item.ventaNoSuj),
    };
  });

  const conditionCode = num(dte.resumen?.condicionOperacion) || 1;

  const parsed: ParsedDtePurchase = {
    meta: {
      tipoDte,
      numeroControl: str(dte.identificacion?.numeroControl),
      codigoGeneracion: str(dte.identificacion?.codigoGeneracion),
      date: str(dte.identificacion?.fecEmi),
    },
    emisor: {
      nit: str(dte.emisor.nit),
      nrc: str(dte.emisor.nrc),
      name: str(dte.emisor.nombre),
      tradeName: str(dte.emisor.nombreComercial) || str(dte.emisor.nombre),
      phone: str(dte.emisor.telefono),
      email: str(dte.emisor.correo),
      activity: str(dte.emisor.descActividad),
      activityCode: str(dte.emisor.codActividad),
      address: str(dte.emisor.direccion?.complemento),
      departmentCode: str(dte.emisor.direccion?.departamento),
      municipalityCode: str(dte.emisor.direccion?.municipio),
    },
    lines,
    payment: {
      condition: mapCondition(conditionCode),
      conditionCode,
    },
    totals: {
      taxedTotal: num(dte.resumen?.totalGravada),
      exemptTotal: num(dte.resumen?.totalExenta),
      nonSubjectTotal: num(dte.resumen?.totalNoSuj),
      discountTotal: num(dte.resumen?.totalDescu),
      subTotal: num(dte.resumen?.subTotal ?? dte.resumen?.subTotalVentas),
      ivaTotal: num(
        (dte.resumen?.tributos ?? []).find((t) => str(t.codigo) === '20')?.valor
      ),
      ivaPerception: num(dte.resumen?.ivaPerci1),
      ivaRetention: num(dte.resumen?.ivaRete1),
      incomeRetention: num(dte.resumen?.reteRenta),
      operationTotal: num(dte.resumen?.montoTotalOperacion),
      payable: num(dte.resumen?.totalPagar),
    },
  };

  return { ok: true, data: parsed };
};

export const tipoDteLabel = (code: string) => TIPO_DTE_LABEL[code] ?? code;
